import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import _ from 'lodash';
import cx from 'classnames';
import { Frame } from "scenejs";

import { EditorContext } from '../../index';

import { selectWidget, toggleEditMode, updateWidgetProperty, updateWidgetEditorState, deleteWidgets, cloneWidgets, bulkUpdateWidgetOrder, bulkUpdateWidgetLayouts, actionGroup, recalculateSectionColumnWidths, splitPageBySelection } from 'actions/editor';
import { Property } from '../../components/properties';
import { PAGE, SECTION_COLUMN } from 'constants/widgetTypes';
import { getMediaInfo } from 'actions/pageHealth';
import { getWidgetType } from 'components/Editor/helper/widget';
import { createWidget } from 'actions/editor';

import { useChildrenWidgetSelected, usePageWidgetSelector, useWidgetChildrenSelector, useAllowCreateLiveElement, useWidgetSelected } from 'components/Editor/hooks/useEditorSession';
import { applyFlexModeToggle, resolveFlexEnabledInTree, CONTENT_DRIVEN_SECTION_HEIGHT, EMPTY_CONTENT_SECTION_MIN_HEIGHT } from '../flex/flexContext';
import { captureFreeformLayouts } from '../flex/flexMeasure';
import { FlexConfigControls } from '../flex/flexConfig';
import Tooltip from 'components/Tooltip';

import { getBoundingRectFromLayout } from 'components/Editor/helper/layoutManager';
import { Background } from 'components/Editor/components/properties/background';
import { LiveElementCreationProperty } from 'components/Editor/components/liveElements';
import StoryboardContext from 'components/Storyboard/context';
import { BackgroundView } from '../background';

import { getBoxShadow } from 'services/cssHelper.service';

import { BrandComponentProperty } from 'components/Editor/components/brandElements';
import userRoleService from 'services/userRoles.service';
import { useMakeFrame } from 'components/Editor/hooks/useMakeFrame';
import { INTERACTIVE_CONTENT_TYPES, PAGE_CONTAIN_STORYBOARDS, STORYBOARD_TYPES, isFlexModeAllowed } from 'constants/storyboardType';

import styles from './section.module.scss';

const IMAGE_CDN_ENDPOINT = process.env.REACT_APP_IMAGE_CDN;

const SectionWidgetViewComponent = function ({ className, style, widgetProps, widgetRef, children, inFlexFlow, parentFlexLayout }) {
  const dispatch = useDispatch();
  const ref = widgetRef;
  const prevBottomRef = useRef(null);

  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;

  const storyboardContext = React.useContext(StoryboardContext);

  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');

  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));
  const childrenSelection = useChildrenWidgetSelected(editor, id);

  const allColumnsFlex = useSelector(state => {
    const widgets = _.get(state, `${editor}.present.widgets`) || [];
    const columns = widgets.filter(w => w.parent === id);
    return columns.length > 0
      && columns.every(column => resolveFlexEnabledInTree(widgets, column, 'desktop'));
  });
  const prevAllColumnsFlexRef = useRef(allColumnsFlex);

  const anyColumnFlex = useSelector(state => {
    const widgets = _.get(state, `${editor}.present.widgets`) || [];
    const columns = widgets.filter(w => w.parent === id);
    const flexColumns = columns.filter(column => resolveFlexEnabledInTree(widgets, column, 'desktop'));
    return flexColumns.length > 0 && flexColumns.length !== columns.length;
  });

  const contentWidgetSignature = useSelector(state => {
    const widgets = _.get(state, `${editor}.present.widgets`) || [];
    const columnIds = new Set(widgets.filter(w => w.parent === id).map(w => w.uid));
    if (columnIds.size === 0) return '';
    return widgets.filter(w => columnIds.has(w.parent)).map(w => w.uid).sort().join(',');
  });

  const sectionHasContent = useSelector(state => {
    const widgets = _.get(state, `${editor}.present.widgets`) || [];
    const columnIds = new Set(widgets.filter(w => w.parent === id).map(w => w.uid));
    if (columnIds.size === 0) return false;
    return widgets.some(w => columnIds.has(w.parent));
  });

  const isFirstFlowedSibling = useSelector(state => {
    const widgets = _.get(state, `${editor}.present.widgets`) || [];
    const parentId = _.get(widgetProps, 'parent');
    const siblings = widgets.filter(w => w.parent === parentId);
    if (siblings.length === 0) return true;
    const minOrder = _.minBy(siblings, w => _.get(w, 'order', 0));
    return _.get(minOrder, 'uid') === id;
  });

  const editorUIState = _.get(widgetProps, 'editorState');
  const stretched = _.get(widgetProps, 'properties.desktop.stretched', false);
  const margin = _.get(widgetProps, 'properties.desktop.margin', 0);
  const layout = _.get(widgetProps, 'properties.desktop.layout');

  const background = _.get(widgetProps, 'properties.desktop.background');

  const columns = _.size(children);
  const columnSpacing = _.get(widgetProps, `properties.${breakpoint}.columnSpacing`, 0);
  const adjustedColumnSpacing = columnSpacing * ((columns - 1) / columns);
  const desktop = breakpoint !== 'mobile';

  var sectionWidgetStyle = {
    ...style,
    ...getStyles(widgetProps, breakpoint, storyboardContext?.type, inFlexFlow, allColumnsFlex, sectionHasContent, parentFlexLayout, isFirstFlowedSibling)
  }

  const padding = Number(_.get(widgetProps, `properties.${breakpoint}.padding`, 0));
  const getBottomBoundary = (section) => {

    let maxBottom = 0;
    const columns = section.querySelectorAll('[data-container="true"]');

    columns.forEach(column => {
      const columnTop = column.getBoundingClientRect().top;
      const children = column.querySelectorAll('[data-widget-id]');

      children.forEach(child => {
        const bottom = child.getBoundingClientRect().bottom;
        maxBottom = Math.max(
          maxBottom,
          Math.ceil(bottom - columnTop)
        );
      });
    });
    return maxBottom;
  };

  useEffect(() => {
    const section = ref.current;
    if (!section) return;

    const wasAllColumnsFlex = prevAllColumnsFlexRef.current;
    prevAllColumnsFlexRef.current = allColumnsFlex;

    if (allColumnsFlex) {
      prevBottomRef.current = null;
      return;
    }

    if (!desktop) {
      section.style.minHeight = '';
      return;
    }

    const minHeight = getBottomBoundary(section) + padding;
    const currentSectionHeight = section.getBoundingClientRect().height;
    const currentHeight = _.get(widgetProps, 'properties.desktop.layout.height');

    const prevBottom = prevBottomRef.current;
    const isResized = !prevBottom || minHeight > prevBottom;
    prevBottomRef.current = minHeight;
    const justLeftFlex = wasAllColumnsFlex && !allColumnsFlex;
    const frozenHeight = justLeftFlex && minHeight <= 0 ? EMPTY_CONTENT_SECTION_MIN_HEIGHT : minHeight;
    const isHeightChanged = justLeftFlex || (isResized && minHeight > 0 && minHeight > currentSectionHeight);

    section.style.minHeight = minHeight > 0 ? `${minHeight}px` : '';

    if (isHeightChanged) {
      section.style.height = `${frozenHeight}px`;

      currentHeight !== `${frozenHeight}px` && dispatch(
        updateWidgetProperty(editor, widgetProps.uid, 'desktop', {
          layout: {
            ...layout,
             height: `${frozenHeight}px`
          }
        })
      );
    }
  }, [childrenSelection, allColumnsFlex]);

  useEffect(() => {
    const section = ref.current;
    if (!section || !anyColumnFlex || !desktop) return;

    const syncHeight = () => {
      const minHeight = getBottomBoundary(section) + padding;
      if (minHeight <= 0) return;

      const currentSectionHeight = section.getBoundingClientRect().height;
      const prevBottom = prevBottomRef.current;
      const isResized = !prevBottom || minHeight > prevBottom;
      prevBottomRef.current = minHeight;

      section.style.minHeight = `${minHeight}px`;

      if (isResized && minHeight > currentSectionHeight) {
        section.style.height = `${minHeight}px`;

        const currentHeight = _.get(widgetProps, 'properties.desktop.layout.height');
        currentHeight !== `${minHeight}px` && dispatch(
          updateWidgetProperty(editor, widgetProps.uid, 'desktop', {
            layout: {
              ...layout,
              height: `${minHeight}px`
            }
          })
        );
      }
    };

    const observer = new ResizeObserver(syncHeight);
    section.querySelectorAll('[data-container="true"] [data-widget-id]').forEach(node => observer.observe(node));
    syncHeight();

    return () => observer.disconnect();
  }, [anyColumnFlex, desktop, childrenSelection, contentWidgetSignature, padding]);

  return (
    <>
      <div
        id={id}
        ref={ref}
        className={cx(className, styles.section, 'widget', {
          [styles.stretched]: stretched,
          [styles.flowed]: inFlexFlow,
          [styles.partialSelection]: childrenSelection.selected && columns > 1,
          [styles.mobile]: breakpoint === 'mobile'
        })}
        style={sectionWidgetStyle}
        data-widget-id={id}
        data-widget-type={type}
        data-selectable={!childrenSelection.selected}
      >
        <BackgroundView background={background} />
        {React.Children.map(children,
          child => {
            return (
              React.cloneElement(child, {
                columnGap: adjustedColumnSpacing
              }, null)
            );
          })
        }
      </div>

      {_.get(editorUIState, 'manageColumns') &&
        <ManageColumnDialog
          id={id}
          parent={id}
          editor={editor}
          widget={widgetProps}
          onCancel={() => {
            dispatch(updateWidgetEditorState(editor, id, {
              manageColumns: false
            }));
          }}
        />
      }
    </>
  );
};

const sectionWidgetEquals = function (prevProps, nextProps) {
  const paths = ['className', 'style', 'widgetProps', 'widgetRef', 'children', 'inFlexFlow', 'parentFlexLayout'];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

const SectionWidgetView = React.memo(SectionWidgetViewComponent, sectionWidgetEquals);

const SectionWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();
  const [view, setView] = useState(null);

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const allowLiveElement = useAllowCreateLiveElement(editorSession, props);
  const storyboardContext = React.useContext(StoryboardContext);

  const allowBrandkit = useSelector((state) =>
    _.get(state, `${editorSession}.present.entityType`)) !== 'PALETTE_COMPONENT';

  const id = _.get(props, 'uid');
  const background = _.get(props, 'properties.desktop.background');

  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  const { makeFrameShow, makeFramePageId } = useMakeFrame({ editorSession });

  useEffect(() => {
    setView(null);
  }, [id])

  const handleLiveElement = () => {
    setView('liveElements')
  }

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  const handleManageColumns = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);

    dispatch(updateWidgetEditorState(editorSession, id, {
      manageColumns: true
    }));
  }

  const handleMakeFrame = () => {
    dispatch(splitPageBySelection(editorSession, makeFramePageId, [id]));
  };

  return (
    <>
      <Property.Action className={styles.textButton} onClick={() => changeView('background')}>
        Change Background
      </Property.Action>
      <Property.Divider />
      <Property.Action className={styles.textButton} onClick={handleManageColumns}>
        Manage Columns
      </Property.Action>
      {makeFrameShow && 
        <>
          <Property.Divider />
          <Property.Action className={styles.textButton} tooltip='Split segment: selection becomes a new frame' onClick={handleMakeFrame}>
            Make Frame
          </Property.Action>
        </>
      }
      <Property.Divider />
      {allowLiveElement &&
        <Property.Action className={styles.iconButton} tooltip='Live Element' onClick={handleLiveElement}>
          <i className={'cleverstory-live-element'} />
        </Property.Action>
      }
      {brandkitEnabled && allowBrandkit && userRoleService.canManageUsers() &&
        <Property.Action className={styles.iconButton} tooltip='Add to Brand Kit' onClick={() => changeView('brandMedia')}>
          <i className={'cleverstory-brandmedia'} />
        </Property.Action>
      }
      <Property.Action className={styles.iconButton} tooltip='Layout' onClick={() => changeView('layout')}>
        <i className={cx("cleverstory-layout")} />
      </Property.Action>

      {!PAGE_CONTAIN_STORYBOARDS.includes(storyboardContext?.type) &&
        <Property.Action className={styles.iconButton} tooltip='Stretch' onClick={() => changeView('stretch')}>
          <i className={cx("cleverstory-stretch")} />
        </Property.Action>
      }

      {!editMode && view === 'liveElements' &&
        <LiveElementCreationProperty elements={[props]} onCancel={() => closePopup()} />
      }

      {!editMode && view === 'background' &&
        <Background.Dialog 
          id={id}
          value={background}
          showShadow={true}
          widgetProps={props}
          onChange={(value) => {
            dispatch(updateWidgetProperty(editorSession, id, 'desktop', {
              background: value
            }));
          }}
          onCancel={() => {
            closePopup();
          }}
        />
      }

      {!editMode && view === 'layout' &&
        <StripLayoutEditor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {!editMode && view === 'stretch' &&
        <StretchSectionDialog
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {view === 'brandMedia' &&
        <BrandComponentProperty type={'SECTIONS'} widgets={[props]} onCancel={() => closePopup()} />
      }
    </>
  );
}

const SectionWidgetMobileAction = (props) => {
  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);
  const allowBrandkit = useSelector((state) =>
    _.get(state, `${editorSession}.present.entityType`)) !== 'PALETTE_COMPONENT';

  const id = _.get(props, 'uid');

  const changeView = (view) => {
    setView(view);
  }

  const closePopup = () => {
    setView(null);
  }

  return (
    <>
      <Property.Action className={styles.iconButton} tooltip='Layout' onClick={() => changeView('layout')}>
        <i className={cx("cleverstory-layout")} />
      </Property.Action>
      <Property.Divider />
      {brandkitEnabled && allowBrandkit && userRoleService.canManageUsers() &&
        <Property.Action className={styles.iconButton} tooltip='Add to Brand Kit' onClick={() => changeView('brandMedia')}>
          <i className={'cleverstory-brandmedia'} />
        </Property.Action>
      }

      {view === 'layout' &&
        <StripLayoutEditor
          id={id}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }
      {view === 'brandMedia' &&
        <BrandComponentProperty type={'SECTIONS'} widgets={[props]} onCancel={() => closePopup()} />
      }
    </>
  );
}

const SectionWidget = React.forwardRef((props, ref) => {
  return <SectionWidgetView {...props} ref={ref} />
});

const ManageColumnDialog = ({ id, parent, widget, position, ...rest }) => {
  const dispatch = useDispatch();
  const store = useStore();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const storyboardType = _.get(React.useContext(StoryboardContext), 'type');
  const entityType = useSelector((state) =>
    _.get(state, `${editorSession}.present.entityType`));

  const [actionState, setActionState] = useState({
    moveLeft: true,
    moveRight: true,
    clone: true,
    deletable: true
  });

  const [flexConfirmFor, setFlexConfirmFor] = useState(null);

  var columns = useWidgetChildrenSelector(editorSession, id);
  columns = _.orderBy(columns, 'order', 'asc');

  const activeColumn = useSelector(state => {
    const selected = _.get(state, `${editorSession}.present.selected`);
    if (_.size(columns) === 0) {
      return null;
    }

    return columns.find(column => {
      return _.includes(selected, column.uid);
    });
  });

  const activeFlexMode = useSelector(state =>
    resolveFlexEnabledInTree(
      _.get(state, `${editorSession}.present.widgets`) || [], activeColumn, 'desktop'));

  const applyFlexMode = (columnId, enabling) => {
    actionGroup.start();
    const widgets = _.get(store.getState(), `${editorSession}.present.widgets`, []) || [];
    const capturedLayouts = enabling ? [] : captureFreeformLayouts(widgets, columnId, 'desktop');
    applyFlexModeToggle(dispatch, editorSession, widgets, columnId, enabling, { capturedLayouts });
    actionGroup.end();
  };

  const handleSelectFluid = () => {
    if (!activeColumn) return;
    setFlexConfirmFor(null);
    if (activeFlexMode) {
      applyFlexMode(activeColumn.uid, false);
    }
  };

  const handleSelectFlex = () => {
    if (!activeColumn || activeFlexMode) return;
    setFlexConfirmFor(activeColumn.uid);
  };

  const handleConfirmFlex = () => {
    if (flexConfirmFor) {
      applyFlexMode(flexConfirmFor, true);
    }
    setFlexConfirmFor(null);
  };

  useEffect(() => {
    const items = columns.map(item => item.uid);
    const index = items.findIndex(item => item === _.get(activeColumn, 'uid'));
  
    if (index === -1) {
      return;
    }
  
    const selected = activeColumn != null;
    const deletable = _.size(items) > 1;
    const cloneable = _.size(items) < 6;
  
    const findLeftUnlocked = () => {
      for (let i = index - 1; i >= 0; i--) {
        if (!columns[i]?.properties?.desktop?.locked) {
          return columns[i];
        }
      }
      return null;
    };
  
    const findRightUnlocked = () => {
      for (let i = index + 1; i < items.length; i++) {
        if (!columns[i]?.properties?.desktop?.locked) {
          return columns[i];
        }
      }
      return null;
    };
  
    const leftColumn = findLeftUnlocked();
    const rightColumn = findRightUnlocked();
  
    setActionState({
      moveLeft: selected && !!leftColumn,
      moveRight: selected && !!rightColumn,
      cloneable: selected && cloneable,
      deletable: selected && deletable
    });
  }, [activeColumn]);
  

  const handleAddColumns = (id) => {
    const columnCount = _.size(columns) + 1;
    const width = 100 / columnCount;

    actionGroup.start();

    dispatch(
      createWidget(
        editorSession,
        [{
          type: SECTION_COLUMN,
          name: "Column",
          properties: {
            "desktop": {
              "visible": true,
              "layout": {
                "transform": {
                  "translate": "0px,0px",
                  "rotate": "0deg"
                },
                "width": `${width}%`,
                "height": "auto"
              },
              "container": {
                "minWidth": 1200 * width / 100,
                "alignment": "center"
              },
              "alignment": null,
              "background": null
            },
            "mobile": {
              "visible": true,
              "layout": {
                "transform": {
                  "translate": "0px, 0px"
                },
                "width": "100%",
                "height": "200px"
              },
              "container": {
                "minWidth": 375,
                "alignment": null
              },
              "alignment": "center",
              "background": null
            },
          },
        }],
        id)
    );

    dispatch(recalculateSectionColumnWidths(editorSession, id));

    actionGroup.end();
  }

  const moveColumn = (delta) => {
    if (!activeColumn) return;
  
    const items = columns.map(item => item.uid);
  
    const unlockedItems = columns
      .filter(col => !col?.properties?.desktop?.locked)
      .map(col => col.uid);
  
    const oldIndex = unlockedItems.findIndex(uid => uid === activeColumn.uid);
    const newIndex = oldIndex + delta;
  
    if (oldIndex === -1 || newIndex < 0 || newIndex >= unlockedItems.length) {
      return;
    }
  
    const reorderedUnlocked = [...unlockedItems];
    reorderedUnlocked.splice(oldIndex, 1);
    reorderedUnlocked.splice(newIndex, 0, activeColumn.uid);
  
    let unlockedPointer = 0;
    const request = items.map((uid, i) => {
      const column = columns.find(c => c.uid === uid);
      if (column?.properties?.desktop?.locked) {
        return { uid, order: i + 1 }; // preserve locked at same index
      } else {
        return { uid: reorderedUnlocked[unlockedPointer++], order: i + 1 };
      }
    });
  
    dispatch(bulkUpdateWidgetOrder(editorSession, request));
  };
  

  const moveLeft = () => {
    moveColumn(-1);
  }

  const moveRight = () => {
    moveColumn(1);
  }

  const cloneColumn = () => {
    actionGroup.start();
    dispatch(cloneWidgets(editorSession, parent, [activeColumn.uid]));
    dispatch(recalculateSectionColumnWidths(editorSession, parent));
    actionGroup.end();
  }

  const deleteColumn = () => {
    dispatch(deleteWidgets(editorSession, [activeColumn.uid]));
  }

  return (
    <Property.Dialog className={styles.manageColumnDialog}
      width={246}
      target={`#${id}`}
      title={'Manage Columns'}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        {_.size(columns) < 6 &&
          <>
            <Property className={styles.buttonAlignmentProperty}>
              <div onClick={() => {
                handleAddColumns(parent);
              }} className={cx(styles.addColumn)}>
                <i className={cx("cleverstory-plus")} />
                <span>Add Column</span>
              </div>
            </Property>
            <Property.Divider />
          </>
        }
        {_.size(columns) > 1 &&
          <Property name="Choose the Column">
            <ColumnSelector editor={editorSession} columns={columns} />
          </Property>
        }
        <Property name="Action">
          <div className={styles.columnActions}>
            <Tooltip placement="top" content="Left">
              <button className={styles.action} disabled={!actionState.moveLeft} onClick={moveLeft}>
                <i className={cx("cleverstory-back")} />
              </button>
            </Tooltip>

            <Tooltip placement="top" content="Right">
              <button className={cx(styles.action, styles.moveRight)}
                disabled={!actionState.moveRight}
                onClick={moveRight}
              >
                <i className={cx("cleverstory-back")} />
              </button>
            </Tooltip>

            <Tooltip placement="top" content="Clone">
              <button className={styles.action} disabled={!actionState.cloneable} onClick={cloneColumn}>
                <i className={cx("cleverstory-clone")} />
              </button>
            </Tooltip>
            <Tooltip placement="top" content="Delete">
              <button className={styles.action} disabled={!actionState.deletable} onClick={deleteColumn}>
                <i className={cx("cleverstory-trash")} />
              </button>
            </Tooltip>
          </div>
        </Property>

        {activeColumn && isFlexModeAllowed(storyboardType, entityType) &&
          <>
            <Property.Divider />
            <Property name="Layout">
              <div className={styles.layoutToggle}>
                <button
                  className={cx(styles.layoutOption, { [styles.active]: !activeFlexMode })}
                  onClick={handleSelectFluid}
                >
                  Freeform
                </button>
                <button
                  className={cx(styles.layoutOption, { [styles.active]: activeFlexMode })}
                  onClick={handleSelectFlex}
                >
                  Flex
                </button>
              </div>
            </Property>

            {activeFlexMode &&
              <>
                <Property.Divider />
                <FlexConfigControls id={activeColumn.uid} widget={activeColumn} />
              </>
            }
          </>
        }

      </Property.Body>

      {flexConfirmFor && activeColumn && flexConfirmFor === activeColumn.uid &&
        <Property.Dialog className={styles.flexConfirmDialog}
          width={246}
          target={`#${activeColumn.uid}`}
          title={'Flex Layout'}
          onCancel={() => setFlexConfirmFor(null)}
        >
          <Property.Body>
            <div className={styles.flexConfirm}>
              <p className={styles.flexConfirmText}>
                Enabling Flex Mode causes all child elements to inherit the parent container's flex behavior.
              </p>
              <div className={styles.flexConfirmActions}>
                <button className={styles.cancel} onClick={() => setFlexConfirmFor(null)}>
                  Cancel
                </button>
                <button className={styles.confirm} onClick={handleConfirmFlex}>
                  Confirm
                </button>
              </div>
            </div>
          </Property.Body>
        </Property.Dialog>
      }
    </Property.Dialog>
  );
}

const ColumnSelector = ({ editor, columns }) => {
  const dispatch = useDispatch();

  const activeColumn = useSelector(state => {
    const selected = _.get(state, `${editor}.present.selected`);
    if (_.size(columns) === 0) {
      return null;
    }

    return columns.find(column => {
      return _.includes(selected, column.uid);
    });
  });

  const handleColumnSelection = (column) => {
    dispatch(selectWidget(editor, [column.uid]));
  }

  return (
    <div className={styles.columnSelector}>
      {columns.map(column => {
        const active = column.uid === _.get(activeColumn, 'uid');
        return <div key={column.uid} className={cx(styles.column, {
          [styles.active]: active
        })} onClick={() => handleColumnSelection(column)}></div>
      })}
    </div>
  );
}


const StripLayoutEditor = ({ id, widget, position, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const mobile = breakpoint === 'mobile';

  var columns = useWidgetChildrenSelector(editorSession, id);
  columns = _.orderBy(columns, 'order', 'asc');

  const properties = _.get(widget, `properties.${breakpoint}`, {});
  const padding = _.get(properties, 'padding', 0);
  const columnSpacing = _.get(properties, 'columnSpacing', 0);

  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, id, breakpoint, value));
  }

  const handlePaddingChange = (value) => {
    handlePropertyChange({
      padding: value
    });
  }

  const handleColumnSpacingChange = (value) => {
    handlePropertyChange({
      columnSpacing: value
    });
  }

  return (
    <Property.Dialog className={styles.sectionLayoutDialog}
      width={246}
      target={`#${id}`}
      title={'Customise Layout'}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        {!mobile &&
          <>
            <Property name="Customise Columns" className={styles.equalColumns}>
              <ColumnResizer editorSession={editorSession} columns={columns} />
            </Property>
            <Property.Divider />
          </>
        }
        <Property name="Column Spacing">
          <Property.Slider
            min={0} max={100} input
            value={columnSpacing}
            onChange={handleColumnSpacingChange}
          />
        </Property>
        <Property name="Padding">
          <Property.Slider
            min={0} max={100} input
            value={padding}
            onChange={handlePaddingChange}
          />
        </Property>

      </Property.Body>
    </Property.Dialog>
  );
}

const ColumnResizer = ({ editorSession, columns }) => {
  const dispatch = useDispatch();

  const ref = React.useRef(null);
  const [state, setState] = useState({
    minWidth: 10,
    columns: {}
  });

  useEffect(() => {
    calculateColumnState();
  }, []);

  const calculateColumnState = () => {
    const totalColumns = _.size(columns);
    const totalWidth = ref.current.clientWidth - (totalColumns - 1) * 5;

    const columnsState = {};
    columns.forEach(column => {
      const id = _.get(column, 'uid');
      const widthValue = parseFloat(_.get(column, 'properties.desktop.layout.width'));

      const width = (widthValue / 100) * totalWidth;
      columnsState[id] = {
        uiWidth: width,
        width: widthValue
      };
    });

    setState({
      minWidth: Math.ceil((5 / 100) * totalWidth),
      columns: columnsState
    });
  }

  const calculateColumnStateForUI = () => {
    const totalColumns = _.size(columns);
    const totalWidth = ref.current.clientWidth - 10 - (totalColumns - 1) * 5;

    const columnsState = {};
    const uiColumns = ref.current.querySelectorAll(`.${styles.column}`);

    uiColumns.forEach(column => {
      const id = column.dataset.column;
      const widthValue = column.clientWidth;

      const width = (widthValue / totalWidth) * 100;
      columnsState[id] = {
        uiWidth: widthValue,
        width: width
      };
    });

    setState({
      minWidth: (5 / 100) * totalWidth,
      columns: columnsState
    });
  }

  const handleDragEnd = () => {
    const totalColumns = _.size(columns);
    const totalWidth = ref.current.clientWidth - 10 - (totalColumns - 1) * 5;

    const changeRequests = columns.map(column => {
      const layout = _.get(column, 'properties.desktop.layout');

      const element = ref.current.querySelector(`.${styles.column}[data-column="${column.uid}"]`);
      const widthValue = element.clientWidth;
      const width = (widthValue / totalWidth) * 100;

      return {
        column: column,
        layout: {
          id: column.uid,
          layout: {
            ...layout,
            width: `${width}%`
          }
        },
        minWidth: 1200 * width / 100
      }
    });

    actionGroup.start();

    const layouts = changeRequests.map(change => change.layout);
    dispatch(bulkUpdateWidgetLayouts(editorSession, 'desktop', layouts));

    changeRequests.forEach(change => {
      dispatch(updateWidgetProperty(editorSession, change.column.uid, 'desktop', {
        container: {
          ..._.get(change.column, 'properties.desktop.container') || {},
          minWidth: change.minWidth
        }
      }));
    });

    actionGroup.end();
  };

  return (
    <div ref={ref} className={styles.columnResizer}>
      {columns.map((column, index) => {
        var columnState = state.columns[column.uid];
        var width = _.get(columnState, 'uiWidth');
        var widthLabel = Math.round(_.get(columnState, 'width'));

        return (
          <React.Fragment key={column.uid}>
            {index != 0 &&
              <DragHandle key={`draggable-${column.uid}`}
                index={index}
                minWidth={state.minWidth}
                onDrag={() => calculateColumnStateForUI()}
                onDragEnd={handleDragEnd}
              />
            }
            <div key={column.uid} className={styles.column}
              data-column={column.uid}
              style={{
                width: width
              }}
            >{`${widthLabel}`}</div>
          </React.Fragment>
        )
      })}
    </div>
  );
}

const DragHandle = ({ index, minWidth, onDragEnd, onDrag }) => {
  const elementRef = React.useRef(null);
  const dragRef = React.useRef({
    dragging: false,
    mouseX: 0,
    startPos: 0,
    startWidthPrev: 0,
    startWidthNext: 0
  });

  useEffect(() => {
    addEventListenersToDocument();

    return () => {
      removeEventListenersFromDocument();
    }
  }, [onDragEnd]);

  const startDrag = () => {
    dragRef.current.dragging = true;
    dragRef.current.startPos = dragRef.current.mouseX;
    dragRef.current.startWidthPrev = 0;
    dragRef.current.startWidthNext = 0;

    if (elementRef.current) {
      let prevSibling = elementRef.current.previousSibling;
      let nextSibling = elementRef.current.nextSibling;

      if (prevSibling) {
        dragRef.current.startWidthPrev = prevSibling.clientWidth;
      }

      if (nextSibling) {
        dragRef.current.startWidthNext = nextSibling.clientWidth;
      }
    }
  }

  const endDrag = () => {
    if (dragRef.current.dragging) {
      onDragEnd();
    }

    dragRef.current.dragging = false;
  }

  const onMouseMove = (e) => {
    dragRef.current.mouseX = e.touches ? e.touches[0].screenX : e.screenX;

    if (!dragRef.current.dragging) {
      return;
    }

    const moveDiff = dragRef.current.startPos - dragRef.current.mouseX;
    let newPrev = dragRef.current.startWidthPrev - moveDiff;
    let newNext = dragRef.current.startWidthNext + moveDiff;

    if (newPrev < minWidth) {
      const offset = newPrev - minWidth;
      newPrev = minWidth;
      newNext += offset;
    } else if (newNext < minWidth) {
      const offset = newNext - minWidth;
      newNext = minWidth;
      newPrev += offset;
    }

    elementRef.current.previousSibling.style.width = newPrev + 'px';
    elementRef.current.nextSibling.style.width = newNext + 'px';

    onDrag();
  }

  const addEventListenersToDocument = () => {
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', endDrag);

    document.addEventListener("touchmove", onMouseMove);
    document.addEventListener("touchend", endDrag);
  }

  const removeEventListenersFromDocument = () => {
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', endDrag);

    document.removeEventListener('touchmove', onMouseMove);
    document.removeEventListener('touchend', endDrag);
  }

  return (
    <div ref={elementRef} className={styles.draggable}
      onMouseDown={startDrag}
      onTouchStart={startDrag}
    ></div>
  );
}

export const StretchSectionDialog = ({ id, widget, position, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const properties = _.get(widget, `properties.desktop`, {});
  const margin = _.get(properties, 'margin', 0);
  const stretched = _.get(properties, 'stretched', false);
  const layout = _.get(widget, `properties.${breakpoint}.layout`);

  const pageWidget = usePageWidgetSelector(editorSession, widget.uid);
  const pageWidth = _.get(pageWidget, 'properties.desktop.container.minWidth');

  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, widget.uid, 'desktop', value));
  }

  const handleMarginChange = (value) => {
    handlePropertyChange({
      margin: value
    });
  }

  const handleSectionSize = (stretched) => {
    var frame = new Frame(layout);
    const rect = getBoundingRectFromLayout(layout);

    if (stretched) {
      frame.remove('width');
      frame.set('transform', 'translate', `0px, ${rect.y}px`);
    } else {
      frame.set('width', `${pageWidth}px`);
      frame.set('transform', 'translate', `0px, ${rect.y}px`);
    }

    handlePropertyChange({
      stretched: stretched,
      layout: frame.get()
    });
  }

  return (
    <Property.Dialog className={styles.stretchSectionDialog}
      width={246}
      target={`#${id}`}
      title={'Stretch Section'}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <Property className={styles.sectionSizeProperty}>
          <div className={styles.sectionSizeContainer}>
            <div className={cx(styles.sectionSize, styles.stretched, {
              [styles.active]: stretched
            })} onClick={() => handleSectionSize(true)}>
              <div className={styles.icon}>
                <div className={styles.inner}></div>
              </div>
              <label>Screen</label>
            </div>
            <div className={cx(styles.sectionSize, {
              [styles.active]: !stretched
            })} onClick={() => handleSectionSize(false)}>
              <div className={styles.icon}>
                <div className={styles.inner}></div>
              </div>
              <label>Page</label>
            </div>
          </div>
        </Property>
        {stretched &&
          <Property name="Margin Spacing" className={styles.marginProperty}>
            <Property.Slider
              min={0}
              max={190}
              step={1}
              input
              value={margin}
              onChange={handleMarginChange}
            />
          </Property>
        }
      </Property.Body>
    </Property.Dialog>
  );
}

SectionWidget.actions = (editorSession, breakpoint, props) => {
  return (
    <>
      {breakpoint === 'desktop' &&
        <SectionWidgetDesktopAction editorSession={editorSession} breakpoint={breakpoint} {...props} />
      }
      {breakpoint === 'mobile' &&
        <SectionWidgetMobileAction editorSession={editorSession} breakpoint={breakpoint} {...props} />
      }
    </>
  );
}

SectionWidget.processRequest = (widget) => {
  var background = _.get(widget, 'properties.desktop.background');

  if (!background) {
    return widget;
  }

  var media = _.get(background, 'media');
  if (media) {
    background = {
      ...background,
      media: media.id
    }
  }

  var layout = _.get(widget, 'properties.desktop.layout');
  const frame = new Frame(layout);

  if (!layout.width) {
    frame.remove('width');
  }

  return {
    ...widget,
    properties: {
      ...widget.properties,
      desktop: {
        ...widget.properties.desktop,
        background: background,
        layout: frame.get()
      }
    }
  }
}

SectionWidget.sizeConfig = async (widget, mediaCache) => {
  const url = _.get(widget, 'properties.desktop.background.media.url', '');
  if (!url) {
    return {
      size: 0,
      type: 'image'
    };
  }
  return {
    size:  await getMediaInfo(`${IMAGE_CDN_ENDPOINT}/${url}`, mediaCache) || 0, // MB
    type: 'image'
  };
}

SectionWidget.moveableConfig = (editorSession, widget, breakpoint) => {
  const desktop = breakpoint === 'desktop';

  return {
    draggable: true,
    resizable: desktop,
    rotatable: false,
    cloneable: true,
    renderDirections: desktop ? ['n', 's'] : [],
    onDrag: (layout) => {
      const frame = new Frame(layout);
      const translate = frame.get("transform", "translate");
      const values = _.split(translate, ",");
      const element = document.querySelector('[data-storyboard-type]');
      const storyboardType = element?.getAttribute('data-storyboard-type');

      var y = parseInt(values[1]);
      if (PAGE_CONTAIN_STORYBOARDS.includes(storyboardType) && storyboardType !== STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2){
        y = Math.max(0, y);
      }

      if(INTERACTIVE_CONTENT_TYPES.includes(storyboardType)){
        frame.set('width', '100%');
      }

      const newTranslate = `0px, ${y}px`;

      if(breakpoint === 'mobile') {
        frame.set('width', '100%');
      }

      frame.set("transform", "translate", newTranslate);
      return frame.get();
    },
    onResize: (widget, layout, breakpoint) => {
      const element = document.querySelector('[data-storyboard-type]');
      const storyboardType = element?.getAttribute('data-storyboard-type');
      const stretched = _.get(widget, 'properties.desktop.stretched');
      const frame = new Frame(layout);
      if(stretched && breakpoint === 'desktop' && !INTERACTIVE_CONTENT_TYPES.includes(storyboardType)){
        const margin = _.get(widget, 'properties.desktop.margin', 0);
        const width = `calc(var(--editor-width) - 2 * ${margin}px)`;
        frame.set('width', width);
        
        return frame.get();
      }

      if(breakpoint === 'mobile'){
        const height = `auto`;
        frame.set('height', height);

        return frame.get();
      }
      
      return frame.get();
    },
    onReparent: (widget, parent) => {
      const type = getWidgetType(editorSession, parent)
      if (type === PAGE) return true;
      return false;
    }
  }
}

function getStyles(widgetProps, breakpoint, storyboardType, inFlexFlow, allColumnsFlex, sectionHasContent, parentFlexLayout, isFirstFlowedSibling) {
  const stretched = _.get(widgetProps, 'properties.desktop.stretched', false);
  const margin = _.get(widgetProps, 'properties.desktop.margin', 0);
  const padding = _.get(widgetProps, `properties.${breakpoint}.padding`, 0);
  const columnSpacing = _.get(widgetProps, `properties.${breakpoint}.columnSpacing`, 0);

  let translateY = _.get(widgetProps, `properties.${breakpoint}.layout.transform.translate`, '')?.split(",")?.[1] || '0px';

  var widgetStyles = {
    marginLeft: 0,
    marginRight: 0,
    paddingTop: `${padding}px`,
    paddingBottom: `${padding}px`,
    transform: `translate(0px, ${translateY})`
  }

  if (allColumnsFlex) {
    widgetStyles = {
      ...widgetStyles,
      height: CONTENT_DRIVEN_SECTION_HEIGHT,
      minHeight: sectionHasContent ? 0 : EMPTY_CONTENT_SECTION_MIN_HEIGHT,
    }
  }

  if (breakpoint === 'desktop') {
    widgetStyles = {
      ...widgetStyles,
      columnGap: `${columnSpacing}px`,
      paddingLeft: `${padding}px`,
      paddingRight: `${padding}px`,
      width: '1200px',
      left: '0px',
    }

    if (inFlexFlow && (!stretched || PAGE_CONTAIN_STORYBOARDS.includes(storyboardType))) {
      widgetStyles = {
        ...widgetStyles,
        '--cs-section-flow-width': '1200px',
      }
    }

    if (stretched && !PAGE_CONTAIN_STORYBOARDS.includes(storyboardType)) {
      const stretchedWidth = `calc(var(--editor-width) - 2 * ${margin}px)`;

      if (inFlexFlow) {
        const isNoWrapRow =
          _.get(parentFlexLayout, 'flexDirection') === 'row' &&
          _.get(parentFlexLayout, 'flexWrap') === 'nowrap';

        if (isNoWrapRow) {
          widgetStyles = {
            ...widgetStyles,
            '--cs-section-flow-width': stretchedWidth,
            marginLeft: isFirstFlowedSibling
              ? `calc((var(--editor-width) - 1200px) / -2)`
              : 0,
            marginRight: 0,
          }
        } else {
          widgetStyles = {
            ...widgetStyles,
            '--cs-section-flow-width': stretchedWidth,
            marginLeft: `calc((100% - (${stretchedWidth})) / 2)`,
            marginRight: `calc((100% - (${stretchedWidth})) / 2)`,
          }
        }
      } else {
        widgetStyles = {
          ...widgetStyles,
          width: `${stretchedWidth} !important`,
          left: `calc((100% - (${stretchedWidth}))/2)`,
        }
      }
    }
  }

  if (breakpoint === 'mobile') {
    widgetStyles = {
      ...widgetStyles,
      rowGap: `${columnSpacing}px`,
    }
  }

  const shadow = _.get(widgetProps, `properties.desktop.shadow`, {});
  const shadowStyle = getBoxShadow(shadow);
  widgetStyles = {
    ...widgetStyles,
    ...shadowStyle
  }

  return widgetStyles;
}

export { SectionWidget };